'use client'

import { useEffect, useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { AlertCircle, CheckCircle2, Info, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  revalidateCreditBalance,
  useCreditPackPurchase,
} from '@/hooks/use-billing'

/** Query keys the credit pack checkout puts on a tool page return address. */
export const TOOL_CREDIT_TOP_UP_PARAM = 'credit_topup'
const PURCHASE_ID_PARAM = 'purchase_id'

/** True when this URL is a credit pack checkout return, not a plan upgrade. */
export function isToolCreditTopUpReturn(
  params: Pick<URLSearchParams, 'has'>,
): boolean {
  return params.has(TOOL_CREDIT_TOP_UP_PARAM)
}

type ReturnState =
  | { kind: 'pending'; purchaseId: string }
  | { kind: 'cancel' }

function readReturn(params: Pick<URLSearchParams, 'get'>): ReturnState | null {
  const value = params.get(TOOL_CREDIT_TOP_UP_PARAM)
  const purchaseId = params.get(PURCHASE_ID_PARAM)
  if (value === 'pending' && purchaseId) return { kind: 'pending', purchaseId }
  if (value === 'cancel') return { kind: 'cancel' }
  return null
}

/**
 * Handles a credit pack checkout return on a tool page.
 *
 * Stripe's redirect is not proof the credits landed: the grant comes from the
 * async webhook. So, like the billing success page, this polls the purchase
 * until it is paid, refreshes the balance only then, and says "credits added"
 * — never "upgrade complete" and never a subscription purchase event. The
 * checkout keys are stripped right away; the rest of the query (the billing
 * workspace) is kept so the page stays in the workspace that paid.
 */
export function ToolCreditTopUpReturn() {
  const params = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const [state, setState] = useState<ReturnState | null>(null)
  const [expired, setExpired] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!isToolCreditTopUpReturn(params)) return
    const next = readReturn(params)
    // One-time hydration from the URL; the keys are stripped right below, so
    // this cannot loop.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (next) setState(next)
    const rest = new URLSearchParams(params.toString())
    rest.delete(TOOL_CREDIT_TOP_UP_PARAM)
    rest.delete(PURCHASE_ID_PARAM)
    const query = rest.toString()
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false })
  }, [params, router, pathname])

  const purchaseId = state?.kind === 'pending' ? state.purchaseId : null
  const purchase = useCreditPackPurchase(purchaseId, {
    enabled: !!purchaseId && !expired,
  })
  const status = purchase.data?.status
  const completed = status === 'paid' || status === 'partially_refunded'
  const failed = status === 'refunded' || status === 'disputed'
  const unavailable = !!purchaseId && !completed && !failed && expired

  useEffect(() => {
    if (!purchaseId || completed || failed) return
    const timer = setTimeout(() => setExpired(true), 60_000)
    return () => clearTimeout(timer)
  }, [purchaseId, completed, failed, attempt])

  useEffect(() => {
    if (completed) void revalidateCreditBalance()
  }, [completed])

  if (!state) return null

  const tone =
    state.kind === 'cancel'
      ? 'muted'
      : completed
        ? 'success'
        : failed || unavailable
          ? 'warning'
          : 'muted'
  const message =
    state.kind === 'cancel'
      ? 'Checkout cancelled — you were not charged.'
      : completed
        ? `Credits added — ${(purchase.data?.effective_credits ?? 0).toLocaleString()} credits are in your balance. You can generate now.`
        : failed
          ? 'This credit purchase was refunded or disputed, so no credits were added.'
          : unavailable
            ? "We couldn't confirm your credit purchase yet. Your credits appear as soon as payment clears."
            : 'Confirming your credit purchase…'

  return (
    <div
      role="status"
      className={`flex items-center justify-center gap-2 px-4 py-3 text-sm ${
        tone === 'success'
          ? 'bg-emerald-50 text-emerald-800'
          : tone === 'warning'
            ? 'bg-amber-50 text-amber-800'
            : 'bg-muted text-muted-foreground'
      }`}
    >
      {state.kind === 'cancel' ? (
        <Info className="size-4 shrink-0" />
      ) : completed ? (
        <CheckCircle2 className="size-4 shrink-0 text-emerald-600" />
      ) : failed || unavailable ? (
        <AlertCircle className="size-4 shrink-0 text-amber-600" />
      ) : (
        <Loader2 className="size-4 shrink-0 animate-spin" />
      )}
      <span>{message}</span>
      {unavailable && (
        <Button
          size="sm"
          variant="outline"
          className="ml-2 h-7 rounded-full"
          onClick={() => {
            setExpired(false)
            setAttempt(value => value + 1)
            void purchase.mutate()
          }}
        >
          Check again
        </Button>
      )}
      {(state.kind === 'cancel' || completed || failed || unavailable) && (
        <button
          type="button"
          aria-label="Dismiss"
          className="ml-2 opacity-60 hover:opacity-100"
          onClick={() => setState(null)}
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  )
}
